import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Send, Sparkles, Image as ImageIcon, Clock, Activity, Wand2 } from "lucide-react";
import dayjs from "dayjs";
import { api, API, imageSourceFor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton, GhostButton, Chip, Field, Card } from "@/components/mo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ZONES = ["Avambraccio", "Spalla", "Braccio intero", "Gamba", "Polpaccio", "Petto", "Schiena", "Costato", "Mano", "Collo", "Altro"];
const SKIN = [{ key: "chiara", label: "Chiara" }, { key: "media", label: "Media" }, { key: "scura", label: "Scura" }];
const DEFAULT_WHATSAPP = "393469582192";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STEP_TITLES = ["PRENOTA", "IL TUO PROGETTO", "ANTEPRIMA & AI", "RIEPILOGO"];

export default function Prenota() {
  const { user } = useAuth();
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: api.getStyles });
  const { data: studio } = useQuery({ queryKey: ["studio"], queryFn: api.getStudio });

  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [selStyles, setSelStyles] = useState([]);
  const [selZones, setSelZones] = useState([]);
  const [idea, setIdea] = useState("");

  const [skinTone, setSkinTone] = useState("media");
  const [refImage, setRefImage] = useState(null); // {b64, preview}
  const [mockupUrl, setMockupUrl] = useState(null);
  const [mockupPath, setMockupPath] = useState(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const goNext = () => {
    if (step === 1 && !name.trim()) {
      toast.error("Inserisci il tuo nome");
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pickReference = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setRefImage({ b64, preview: b64 });
    setMockupUrl(null);
    setMockupPath(null);
  };

  const runMockup = async () => {
    if (!refImage) {
      toast.error("Carica un'immagine di riferimento del tatuaggio");
      return;
    }
    setMockupLoading(true);
    try {
      const res = await api.aiMockup({
        tattooImageBase64: refImage.b64,
        bodyPart: selZones[0] || "Avambraccio",
        style: selStyles.join(", "),
        skinTone,
      });
      setMockupUrl(imageSourceFor({ imagePath: res.path }) || res.url);
      setMockupPath(res.path);
      toast.success("Mockup generato!");
    } catch (err) {
      toast.error("Generazione mockup non riuscita");
    } finally {
      setMockupLoading(false);
    }
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await api.aiConsultation({
        clientName: name.trim() || "Ospite Studio",
        contactInfo: phone.trim(),
        bodyPart: selZones.join(", ") || "Da definire",
        tattooStyle: selStyles.join(", ") || "Personalizzato",
        userPreferences: idea.trim(),
      });
      setReport(res.result);
      toast.success("Consulenza AI completata!");
    } catch {
      toast.error("Errore durante l'analisi AI");
    } finally {
      setAiLoading(false);
    }
  };

  const buildMessage = () =>
    `*Richiesta Appuntamento Mörphösy*\n\n` +
    `Nome: ${name.trim() || "-"}\n` +
    `Cellulare: ${phone.trim() || "-"}\n` +
    (email.trim() ? `Email: ${email.trim()}\n` : "") +
    `Stili: ${selStyles.join(", ") || "-"}\n` +
    `Zone: ${selZones.join(", ") || "-"}\n` +
    `Idea: ${idea.trim() || "-"}\n` +
    (mockupPath ? `\nMockup generato: ${API}/files/${mockupPath}\n` : "") +
    `\n` +
    `Inviato dal sito Mörphösy`;

  const sendRequest = async () => {
    if (!privacy) {
      toast.error("Per inviare la richiesta devi accettare l'informativa privacy");
      return;
    }
    const waNumber = (studio?.whatsapp || DEFAULT_WHATSAPP).replace(/\D/g, "");
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(buildMessage())}`;
    // Open synchronously within the click so the browser doesn't block it as a popup
    const waWindow = window.open("", "_blank");
    setSending(true);
    try {
      await api.bookAppointment({
        clientName: name.trim() || "Cliente Studio",
        date: dayjs().format("YYYY-MM-DD"),
        time: "--:--",
        description: `Stili: ${selStyles.join(", ") || "-"} · Zone: ${selZones.join(", ") || "-"} · ${idea.trim()}`.slice(0, 400),
        contactInfo: phone.trim(),
        status: "Richiesto",
        mockupPath,
        privacyAccepted: privacy,
      });
      toast.success("Richiesta salvata! Completa l'invio su WhatsApp.");
      if (user) toast.message("La trovi nel tuo profilo", { description: "Sezione Profilo → I miei appuntamenti" });
      setStep(1);
      setSelStyles([]);
      setSelZones([]);
      setIdea("");
      setRefImage(null);
      setMockupUrl(null);
      setMockupPath(null);
      setReport(null);
      setPrivacy(false);
    } catch {
      toast.error("Errore nel salvataggio, invio comunque su WhatsApp");
    } finally {
      setSending(false);
      if (waWindow) waWindow.location.href = waUrl;
      else window.location.href = waUrl;
    }
  };

  return (
    <div className="mo-rise px-5 pt-6 md:px-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={goBack} disabled={step === 1} data-testid="wizard-back" className="flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-30">
          <ChevronLeft className="h-5 w-5 text-mo-ink" />
        </button>
        <span className="font-body text-[13px] font-extrabold tracking-[0.2em] text-mo-ink">{STEP_TITLES[step - 1]}</span>
        <div className="h-10 w-10" />
      </div>

      {/* Progress */}
      <div className="my-5 flex items-center">
        {[1, 2, 3, 4].map((n) => (
          <React.Fragment key={n}>
            <div className={"flex h-7 w-7 items-center justify-center rounded-full border font-body text-xs font-bold " + (step >= n ? "border-mo-brand bg-mo-brand text-mo-onbrand" : "border-mo-line bg-mo-surface2 text-mo-muted")}>
              {n}
            </div>
            {n < 4 ? <div className={"mx-1 h-px flex-1 " + (step > n ? "bg-mo-brand" : "bg-mo-line")} /> : null}
          </React.Fragment>
        ))}
      </div>

      <div className="flex flex-col gap-4 pb-6">
        {step === 1 && (
          <>
            <h2 className="font-display text-3xl font-bold text-mo-ink">I tuoi dati</h2>
            <Field data-testid="wizard-name" className="!text-black" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e Cognome" />
            <Field data-testid="wizard-phone" className="!text-black" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Cellulare" />
            <Field data-testid="wizard-email" className="!text-black" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (opzionale)" />
            <h3 className="mt-3 font-display text-2xl font-bold text-mo-ink">Stile artistico</h3>
            <p className="font-body text-[13px] text-mo-muted">Seleziona lo stile che preferisci (o più di uno)</p>
            <div className="flex flex-wrap gap-2">
              {styles.map((s) => (
                <Chip key={s.id} className="uppercase tracking-wider" label={s.name} selected={selStyles.includes(s.name)} onClick={() => toggle(selStyles, setSelStyles, s.name)} data-testid={`wizard-style-${s.id}`} />
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="font-display text-3xl font-bold text-mo-ink">Zona del corpo</h2>
            <p className="font-body text-[13px] text-mo-muted">Seleziona la zona (puoi sceglierne più di una)</p>
            <div className="flex flex-wrap gap-2">
              {ZONES.map((z) => (
                <Chip key={z} label={z} selected={selZones.includes(z)} onClick={() => toggle(selZones, setSelZones, z)} data-testid={`wizard-zone-${z}`} />
              ))}
            </div>
            <h3 className="mt-3 font-display text-2xl font-bold text-mo-ink">Descrivi la tua idea</h3>
            <Field textarea data-testid="wizard-idea" value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="Raccontaci il tuo progetto…" />
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="font-display text-3xl font-bold text-mo-ink">Anteprima mockup AI</h2>
            <p className="font-body text-[13px] text-mo-muted">
              Carica un'immagine di riferimento: l'AI la posizionerà sulla zona scelta ({selZones[0] || "nessuna zona selezionata"}) su un modello 3D del corpo con pelle realistica.
            </p>

            <Card className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-body text-xs font-bold text-mo-ink">Tono pelle:</span>
                {SKIN.map((s) => (
                  <Chip key={s.key} label={s.label} selected={skinTone === s.key} onClick={() => setSkinTone(s.key)} data-testid={`skin-${s.key}`} />
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="mb-1.5 font-body text-xs font-bold text-mo-ink">Riferimento</p>
                  <label className="flex h-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-mo-line bg-mo-input" data-testid="wizard-ref-upload">
                    {refImage ? (
                      <img src={refImage.preview} alt="ref" className="h-full w-full object-contain" />
                    ) : (
                      <span className="flex flex-col items-center gap-1 text-mo-muted">
                        <ImageIcon className="h-6 w-6" />
                        <span className="font-body text-xs">Carica tatuaggio</span>
                      </span>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={pickReference} />
                  </label>
                </div>
                <div>
                  <p className="mb-1.5 font-body text-xs font-bold text-mo-ink">Mockup generato</p>
                  <div className="flex h-40 items-center justify-center overflow-hidden rounded-xl border border-mo-line bg-mo-surface3" data-testid="wizard-mockup-result">
                    {mockupUrl ? (
                      <img src={mockupUrl} alt="mockup" className="h-full w-full object-cover" />
                    ) : (
                      <span className="font-body text-xs text-mo-muted">In attesa…</span>
                    )}
                  </div>
                </div>
              </div>

              <GhostButton icon={Wand2} onClick={runMockup} loading={mockupLoading} data-testid="wizard-mockup-btn">
                Genera mockup sul corpo
              </GhostButton>
            </Card>

            <PrimaryButton icon={Sparkles} onClick={runAi} loading={aiLoading} data-testid="wizard-ai">
              Analizza con Consulenza AI
            </PrimaryButton>

            {report && (
              <Card className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 text-mo-brand" />
                  <div>
                    <p className="font-body text-xs font-bold uppercase tracking-wide text-mo-brand">Ore di lavoro stimate</p>
                    <p className="font-body text-sm text-mo-ink">{report.workHours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Activity className="mt-0.5 h-5 w-5 text-mo-brand" />
                  <div className="flex-1">
                    <p className="font-body text-xs font-bold uppercase tracking-wide text-mo-brand">Soglia del dolore · {report.painLevel}/10</p>
                    <div className="my-1.5 h-2 w-full overflow-hidden rounded-full bg-mo-surface3">
                      <div className="h-full rounded-full bg-mo-brand" style={{ width: `${report.painLevel * 10}%` }} />
                    </div>
                    <p className="font-body text-sm text-mo-ink">{report.painDescription}</p>
                  </div>
                </div>
                {report.advice?.length ? (
                  <div>
                    <p className="mb-1 font-body text-xs font-bold uppercase tracking-wide text-mo-brand">Consigli</p>
                    <ul className="list-disc space-y-1 pl-5 font-body text-sm text-mo-ink">
                      {report.advice.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                ) : null}
                {report.meaning ? <p className="font-body text-sm italic text-mo-muted">{report.meaning}</p> : null}
              </Card>
            )}
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="font-display text-3xl font-bold text-mo-ink">Riepilogo richiesta</h2>
            <Card className="flex flex-col gap-2">
              {[["Nome", name.trim() || "-"], ["Cellulare", phone.trim() || "-"], ...(email.trim() ? [["Email", email.trim()]] : []), ["Stili", selStyles.join(", ") || "-"], ["Zone", selZones.join(", ") || "-"], ["Idea", idea.trim() || "-"]].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-3">
                  <span className="font-body text-[13px] text-mo-muted">{l}</span>
                  <span className="flex-1 text-right font-body text-[13px] font-bold text-mo-ink">{v}</span>
                </div>
              ))}
            </Card>
            {mockupUrl && <img src={mockupUrl} alt="mockup" className="rounded-xl border border-mo-line" />}
            <p className="font-body text-[13px] text-mo-muted">
              La richiesta verrà inviata via WhatsApp allo studio e registrata{user ? " nel tuo profilo" : ". Accedi per salvarla nel tuo profilo"}.
            </p>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-mo-line bg-mo-card p-4" data-testid="privacy-consent">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                data-testid="privacy-checkbox"
                className="mt-0.5 h-5 w-5 flex-shrink-0 accent-[#E6C79E]"
              />
              <span className="font-body text-[13px] leading-relaxed text-mo-ink">
                Ho letto l'<button type="button" onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }} data-testid="privacy-open" className="font-bold text-mo-brand underline underline-offset-2">
                  informativa privacy
                </button>{" "}
                e acconsento al trattamento dei miei dati personali (Reg. UE 2016/679 – GDPR) per la gestione della richiesta di appuntamento.
              </span>
            </label>
          </>
        )}
      </div>

      {/* Footer CTA */}
      <div className="sticky bottom-20 z-30 border-t border-mo-line bg-mo-surface/90 py-4 backdrop-blur md:bottom-0">
        {step < 4 ? (
          <PrimaryButton icon={ChevronRight} onClick={goNext} data-testid="wizard-next" className="w-full">
            Avanti
          </PrimaryButton>
        ) : (
          <PrimaryButton icon={Send} onClick={sendRequest} loading={sending} disabled={!privacy} data-testid="wizard-send" className="w-full">
            Invia richiesta via WhatsApp
          </PrimaryButton>
        )}
      </div>

      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-mo-line bg-mo-surface2 text-mo-ink" data-testid="privacy-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-mo-ink">Informativa sulla privacy</DialogTitle>
            <DialogDescription className="font-body text-xs text-mo-muted">Ai sensi degli artt. 13-14 del Regolamento UE 2016/679 (GDPR)</DialogDescription>
          </DialogHeader>
          <PrivacyText studio={studio} />
          <PrimaryButton onClick={() => { setPrivacy(true); setPrivacyOpen(false); }} data-testid="privacy-accept" className="w-full">
            Ho letto e accetto
          </PrimaryButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PrivacyText({ studio }) {
  const owner = `Studio ${studio?.name || "Mörphösy"}`;
  const items = [
    ["Titolare del trattamento", `${owner}${studio?.address ? `, ${studio.address}` : ""}${studio?.email ? ` – ${studio.email}` : ""}.`],
    ["Dati trattati", "Nome e cognome, numero di cellulare, email (facoltativa), preferenze sul tatuaggio (stile, zona del corpo, idea) e le immagini di riferimento e i mockup generati."],
    ["Finalità e base giuridica", "Gestione della richiesta di appuntamento e i contatti successivi (art. 6.1.b GDPR – misure precontrattuali) sulla base del tuo consenso (art. 6.1.a)."],
    ["Modalità e destinatari", "I dati sono trattati con strumenti elettronici e protetti da misure di sicurezza adeguate. La richiesta viene inoltrata allo studio tramite WhatsApp (Meta Platforms). Le immagini sono elaborate da servizi di intelligenza artificiale per generare il mockup. I dati non vengono diffusi."],
    ["Conservazione", "Per il tempo necessario a gestire la richiesta e il rapporto con lo studio, e comunque non oltre 24 mesi dall'ultimo contatto, salvo obblighi di legge."],
    ["I tuoi diritti", "Puoi chiedere l'accesso, la rettifica, la cancellazione, la limitazione, la portabilità dei dati e opporti al trattamento, revocare il consenso in ogni momento e presentare reclamo al Garante per la protezione dei dati personali (garanteprivacy.it)."],
  ];
  return (
    <div className="flex flex-col gap-3">
      {items.map(([t, d]) => (
        <div key={t}>
          <p className="font-body text-[13px] font-bold text-mo-brand">{t}</p>
          <p className="font-body text-[13px] leading-relaxed text-mo-ink">{d}</p>
        </div>
      ))}
    </div>
  );
}
